import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./ServiceCoverflowCarousel.css";
import laundry from "../assets/new_image/laundry.png";
import maidWithJadu from "../assets/new_image/maidWIthJadu.png";
import japa from "../assets/new_image/Japa.png";
import cook from "../assets/new_image/maid.png";
import allRounder from "../assets/new_image/AllRounder.png";
import babysitter from "../assets/new_image/babysitter.png";

/**
 * ServiceCoverflowCarousel
 * ------------------------------------------------------------------
 * A premium, center-focused 3D coverflow carousel for service
 * categories (Laundry, Maid, Japa, Cook, Babysitter, ...).
 *
 * PROPS
 * ------------------------------------------------------------------
 * services   : Array<{ id: string|number, name: string, image: string }>
 * autoPlay   : boolean  (default true)
 * interval   : number   (ms, default 3000)
 * onChange   : (service, index) => void   (optional, fires when active card changes)
 * className  : string   (optional extra class on the root wrapper)
 */

// Shortest signed circular distance between two indices in a ring of length n
function circularOffset(index, activeIndex, n) {
  let diff = index - activeIndex;
  if (diff > n / 2) diff -= n;
  if (diff < -n / 2) diff += n;
  return diff;
}

const DEFAULT_SERVICES = [
  { id: "laundry", name: "Laundry", image: laundry },
  { id: "maid", name: "Maid", image: maidWithJadu },
  { id: "japa", name: "Japa", image: japa },
  { id: "cook", name: "Cook", image: cook },
  { id: "AllRounder", name: "AllRounder", image: allRounder },
  { id: "babysitter", name: "Babysitter", image: babysitter },
];

export default function ServiceCoverflowCarousel({
  services,
  autoPlay = true,
  interval = 3000,
  onChange,
  className = "",
}) {
  const items =
    services && services.length > 0 ? services : DEFAULT_SERVICES;
  const count = items.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const timerRef = useRef(null);
  const trackRef = useRef(null);
  const dragState = useRef({ startX: 0, dragging: false });

  const goTo = useCallback(
    (index) => {
      const normalized = ((index % count) + count) % count;
      setActiveIndex(normalized);
    },
    [count]
  );

  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  // Notify parent when active service changes
  useEffect(() => {
    if (onChange) onChange(items[activeIndex], activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Autoplay, paused on hover / drag
  useEffect(() => {
    if (!autoPlay || isHovering || count <= 1) return undefined;
    timerRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % count);
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, isHovering, interval, count]);

  // Keyboard navigation (left/right) when carousel is focused
  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
  };

  // Pointer / touch swipe support
  const handlePointerDown = (e) => {
    dragState.current.dragging = true;
    dragState.current.startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
  };

  const handlePointerUp = (e) => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;
    const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
    const deltaX = endX - dragState.current.startX;
    const SWIPE_THRESHOLD = 40;
    if (deltaX > SWIPE_THRESHOLD) prev();
    else if (deltaX < -SWIPE_THRESHOLD) next();
  };

  // Card sizing / motion rules per offset from the active (center) card
  const getCardStyle = (offset) => {
    const abs = Math.abs(offset);
    if (abs > 2) {
      return { x: offset > 0 ? 900 : -900, scale: 0.5, opacity: 0, zIndex: 0 };
    }
    const spacing = 200; // horizontal distance between card centers
    const scaleSteps = [1, 0.78, 0.6]; // center, 1-away, 2-away
    const opacitySteps = [1, 0.85, 0.55];
    const rotateSteps = [0, 22, 32]; // subtle 3D perspective tilt

    const scale = scaleSteps[abs];
    const opacity = opacitySteps[abs];
    const rotateY = offset === 0 ? 0 : offset > 0 ? -rotateSteps[abs] : rotateSteps[abs];

    return {
      x: offset * spacing,
      scale,
      opacity,
      rotateY,
      zIndex: 10 - abs,
    };
  };

  return (
    <div
      className={`coverflow-wrapper ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className="coverflow-track"
        ref={trackRef}
        tabIndex={0}
        role="region"
        aria-label="Service category carousel"
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchEnd={handlePointerUp}
      >
        <button
          className="coverflow-arrow coverflow-arrow-left"
          onClick={prev}
          aria-label="Previous service"
          type="button"
        >
          &#8249;
        </button>

        <div className="coverflow-stage">
          {items.map((service, index) => {
            const offset = circularOffset(index, activeIndex, count);
            if (Math.abs(offset) > 2) return null; // keep DOM light
            const style = getCardStyle(offset);
            const isActive = offset === 0;
            const image = service.image;

            return (
              <motion.div
                key={service.id}
                className={`coverflow-card ${isActive ? "is-active" : ""}`}
                style={{ zIndex: style.zIndex }}
                initial={false}
                animate={{
                  x: style.x,
                  scale: style.scale,
                  opacity: style.opacity,
                  rotateY: style.rotateY,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.9,
                }}
                onClick={() => goTo(index)}
              >
                <div className="coverflow-card-inner">
                  <img
                    src={image}
                    alt={service.name}
                    className="coverflow-card-image"
                    draggable={false}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <button
          className="coverflow-arrow coverflow-arrow-right"
          onClick={next}
          aria-label="Next service"
          type="button"
        >
          &#8250;
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={items[activeIndex]?.id}
          className="coverflow-label"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {items[activeIndex]?.name}
        </motion.p>
      </AnimatePresence>

      <div className="coverflow-dots">
        {items.map((service, index) => (
          <button
            key={service.id}
            type="button"
            className={`coverflow-dot ${index === activeIndex ? "is-active" : ""}`}
            aria-label={`Go to ${service.name}`}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  );
}